import { useCallback, useState } from "react";

/**
 * Which of a repeated group's rows are open.
 *
 * A collapsed row is not merely skipped by the keyboard — MUI's `Collapse`
 * leaves its fields mounted but `visibility: hidden`, so nothing inside can take
 * focus at all. Tab passes over it, and travel to a flagged field inside it
 * focuses an element that cannot be focused and silently does nothing. Which
 * rows start open is therefore a question about whether the form is reachable,
 * not only about how much of it is on screen.
 *
 * Only the reviewer's own toggles are stored. The default is derived on every
 * read rather than seeded at mount, because what it depends on arrives from a
 * query: a set captured in a `useState` initializer would freeze every row shut
 * before the offers land and never reopen.
 *
 * The flip side is that the default must not change while the reviewer works, or
 * a row would fold up under them mid-edit. That rules out deriving it from
 * anything they answer — tabbing off a flagged field acknowledges it, so a
 * default reading attention would close the row being tabbed through.
 */
export function useRowExpansion(
  lineNumbers: readonly string[],
  /** Whether a row the reviewer has said nothing about starts open. Keep it stable. */
  openByDefault: (lineNumber: string) => boolean
): {
  isExpanded: (lineNumber: string) => boolean;
  setExpanded: (lineNumber: string, expanded: boolean) => void;
  allExpanded: boolean;
  toggleAll: () => void;
} {
  const [toggled, setToggled] = useState<ReadonlyMap<string, boolean>>(() => new Map());

  const isExpanded = useCallback(
    (lineNumber: string) => toggled.get(lineNumber) ?? openByDefault(lineNumber),
    [toggled, openByDefault]
  );

  const setExpanded = useCallback(
    (lineNumber: string, expanded: boolean) =>
      setToggled((previous) => new Map(previous).set(lineNumber, expanded)),
    []
  );

  const allExpanded = lineNumbers.length > 0 && lineNumbers.every(isExpanded);

  // Written for every row rather than cleared back to the default, so "Collapse
  // all" holds on a form whose default would otherwise reopen them immediately.
  const toggleAll = useCallback(() => {
    const next = !allExpanded;
    setToggled((previous) => {
      const updated = new Map(previous);
      for (const lineNumber of lineNumbers) updated.set(lineNumber, next);
      return updated;
    });
  }, [allExpanded, lineNumbers]);

  return { isExpanded, setExpanded, allExpanded, toggleAll };
}
